"use client";

import * as React from "react";
import { Clock } from "lucide-react";

import {
  formatTimeLabel,
  generateTimeSlots,
  groupTimeSlots,
} from "@/lib/booking/time-slots";
import { cn } from "@/lib/utils";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

export type TimePickerProps = {
  value?: number;
  defaultValue?: number;
  onChange?: (minutes: number) => void;
  /** Popover title, e.g. "Select pickup time" */
  title?: string;
  /** Opening hours in minutes from midnight */
  openingStart?: number;
  openingEnd?: number;
  /** Slot interval in minutes @default 30 */
  intervalMinutes?: number;
  placeholder?: string;
  className?: string;
  id?: string;
  disabled?: boolean;
  align?: React.ComponentProps<typeof PopoverContent>["align"];
};

export function TimePicker({
  value,
  defaultValue,
  onChange,
  title = "Select time",
  openingStart = 0,
  openingEnd = 24 * 60 - 30,
  intervalMinutes = 30,
  placeholder = "Time",
  className,
  id,
  disabled,
  align = "start",
}: TimePickerProps) {
  const [internalValue, setInternalValue] = React.useState<number | undefined>(
    defaultValue,
  );
  const [open, setOpen] = React.useState(false);

  const isControlled = value !== undefined;
  const selectedMinutes = isControlled ? value : internalValue;

  const slots = React.useMemo(
    () => generateTimeSlots(openingStart, openingEnd, intervalMinutes),
    [openingStart, openingEnd, intervalMinutes],
  );

  const slotGroups = React.useMemo(() => groupTimeSlots(slots), [slots]);

  const handleSelect = (minutes: number) => {
    if (!isControlled) {
      setInternalValue(minutes);
    }
    onChange?.(minutes);
    setOpen(false);
  };

  const displayValue =
    selectedMinutes !== undefined ? formatTimeLabel(selectedMinutes) : "";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        disabled={disabled}
        nativeButton={false}
        render={
          <input
            id={id}
            type="text"
            readOnly
            disabled={disabled}
            value={displayValue}
            placeholder={placeholder}
            className={cn("cursor-pointer", className)}
            aria-label={title}
          />
        }
      />
      <PopoverContent
        align={align}
        className="w-[min(100vw-2rem,22rem)] gap-0 overflow-hidden p-0"
        sideOffset={8}
      >
        <p className="border-b border-border px-4 py-3 text-center text-base font-medium text-foreground">
          {title}
        </p>
        <div className="flex items-center gap-2 border-b border-border px-4 py-2.5 text-sm text-muted-foreground">
          <Clock className="size-4 shrink-0" aria-hidden />
          <span>
            Opening Times: {formatTimeLabel(openingStart)} -{" "}
            {formatTimeLabel(openingEnd)}
          </span>
        </div>
        <div className="max-h-[min(28rem,70vh)] overflow-y-auto overscroll-contain px-3 py-3">
          {slotGroups.map((group, groupIndex) => (
            <div
              key={group.period}
              className={cn(groupIndex > 0 && "mt-4")}
            >
              <p className="mb-2 text-sm font-semibold text-foreground">
                {group.period}
              </p>
              <div className="grid grid-cols-2 gap-2">
                {group.slots.map((minutes) => {
                  const isSelected = selectedMinutes === minutes;
                  return (
                    <button
                      key={minutes}
                      type="button"
                      onClick={() => handleSelect(minutes)}
                      className={cn(
                        "rounded-md px-2 py-2.5 text-sm font-medium transition-colors cursor-pointer",
                        isSelected
                          ? "bg-foreground text-background "
                          : "bg-muted/80 text-foreground hover:bg-gray-200",
                      )}
                    >
                      {formatTimeLabel(minutes)}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export { formatTimeLabel };
